import { Action, Reducer } from 'redux';
import { AppThunkAction } from './';
import { get, post, remove } from '../fetch-interceptor';
import { AIType } from './Tenant';

// State definition
export interface AiAgentsState {
    clientUrl: string;
    settings: AiAgentsSettings;
    tenantStatus: AiAgentsTenantStatus;
    isLoading: boolean;
    error: string | null;
}

export interface AiAgentsSettings {
    type: AIType;
    openAIApiKey: string;
    modelId: string;
    endpoint: string;
}

export enum TenantStatus {
    NotConfigured = 'NotConfigured',
    CreationQueued = 'CreationQueued',
    Creating = 'Creating',
    Created = 'Created',
    Deleting = 'Deleting',
    Error = 'Error'
}

export interface AiAgentsTenantStatus {
    hasTenant: boolean;
    status: TenantStatus;
    lastStatusChange: string | null;
    errorMessage: string | null;
}

// Action types
export enum ActionTypes {
    REQUEST_SETTINGS = 'REQUEST_AIAGENTS_SETTINGS',
    RECEIVE_SETTINGS = 'RECEIVE_AIAGENTS_SETTINGS',
    REQUEST_UPDATE_SETTINGS = 'REQUEST_UPDATE_AIAGENTS_SETTINGS',
    REQUEST_TENANT_STATUS = 'REQUEST_AIAGENTS_TENANT_STATUS',
    RECEIVE_TENANT_STATUS = 'RECEIVE_AIAGENTS_TENANT_STATUS',
    SET_ERROR = 'SET_AIAGENTS_ERROR',
    CLEAR_ERROR = 'CLEAR_AIAGENTS_ERROR'
}

// Action interfaces
interface RequestSettingsAction {
    type: ActionTypes.REQUEST_SETTINGS;
}

interface ReceiveSettingsAction {
    type: ActionTypes.RECEIVE_SETTINGS;
    settings: AiAgentsSettings;
}
interface RequestTenantStatusAction {
    type: ActionTypes.REQUEST_TENANT_STATUS;
}

interface ReceiveTenantStatusAction {
    type: ActionTypes.RECEIVE_TENANT_STATUS;
    status: AiAgentsTenantStatus;
}

interface SetErrorAction {
    type: ActionTypes.SET_ERROR;
    error: string;
}

interface ClearErrorAction {
    type: ActionTypes.CLEAR_ERROR;
}

export type KnownAction =
    | RequestSettingsAction
    | ReceiveSettingsAction
    | RequestTenantStatusAction
    | ReceiveTenantStatusAction
    | SetErrorAction
    | ClearErrorAction;

// Action creators
export const actionCreators = {
    requestSettings: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        dispatch({ type: ActionTypes.REQUEST_SETTINGS });
        
        try {
            const settings = await get<AiAgentsSettings>('/api/aiagents/settings');
            
            dispatch({ 
                type: ActionTypes.RECEIVE_SETTINGS, 
                settings: settings
            });
        } catch (error) {
            console.error('Failed to load AI Agents settings', error);
            dispatch({ 
                type: ActionTypes.SET_ERROR, 
                error: 'Failed to load AI Agents settings' 
            });
        }
    },
    
    updateSettings: (settings: AiAgentsSettings, onSuccess?: () => void): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        dispatch({ type: ActionTypes.REQUEST_SETTINGS });
        
        try {
            // Create a copy without the isEnabled property
            const settingsToUpdate = {
                type: settings.type,
                openAIApiKey: settings.openAIApiKey,
                modelId: settings.modelId,
                endpoint: settings.endpoint
            };
            
            const updatedSettings = await post<AiAgentsSettings>('/api/aiagents/settings', settingsToUpdate);
            
            dispatch({
                type: ActionTypes.RECEIVE_SETTINGS,
                settings: updatedSettings
            });
            onSuccess?.();
        } catch (error) {
            console.error('Failed to update AI Agents settings', error);
            dispatch({ 
                type: ActionTypes.SET_ERROR, 
                error: (error as any).response?.data || 'Failed to update AI Agents settings' 
            });
        }
    },
    
    getTenantStatus: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        dispatch({ type: ActionTypes.REQUEST_TENANT_STATUS });
        
        try {
            const status = await get<AiAgentsTenantStatus>('/api/aiagents/status');
            
            dispatch({ 
                type: ActionTypes.RECEIVE_TENANT_STATUS, 
                status: status 
            });
        } catch (error) {
            console.error('Failed to get AI Agents tenant status', error);
            dispatch({ 
                type: ActionTypes.SET_ERROR, 
                error: 'Failed to get AI Agents tenant status' 
            });
        }
    },
    
    clearError: (): AppThunkAction<KnownAction> => (dispatch) => {
        dispatch({ type: ActionTypes.CLEAR_ERROR });
    },

    setError: (error: string): AppThunkAction<KnownAction> => (dispatch) => {
        dispatch({ type: ActionTypes.SET_ERROR, error });
    }
};

// Reducer
const initialState: AiAgentsState = {
    clientUrl: '',
    settings: {
        type: AIType.Ppmx,
        openAIApiKey: '',
        modelId: '',
        endpoint: ''
    },
    tenantStatus: {
        hasTenant: false,
        status: TenantStatus.NotConfigured,
        lastStatusChange: null,
        errorMessage: null
    },
    isLoading: false,
    error: null
};

export const reducer: Reducer<AiAgentsState> = (state: AiAgentsState = initialState, incomingAction: Action): AiAgentsState => {
    const action = incomingAction as KnownAction;
    
    switch (action.type) {
        case ActionTypes.REQUEST_SETTINGS:
            return {
                ...state,
                isLoading: true,
                error: null
            };
            
        case ActionTypes.RECEIVE_SETTINGS:
            return {
                ...state,
                settings: action.settings,
                isLoading: false
            };
            
        case ActionTypes.REQUEST_TENANT_STATUS:
            return {
                ...state,
                isLoading: true
            };
            
        case ActionTypes.RECEIVE_TENANT_STATUS:
            return {
                ...state,
                tenantStatus: action.status,
                isLoading: false
            };
            
        case ActionTypes.SET_ERROR:
            return {
                ...state,
                isLoading: false,
                error: action.error
            };
            
        case ActionTypes.CLEAR_ERROR:
            return {
                ...state,
                error: null
            };
    }
    
    return state;
};
